/**
 * Optimized Navbar Component
 * Single menu structure for both desktop and mobile
 */

export class OptimizedNavbar {
    private navbar: HTMLElement | null;
    private mobileMenuToggle: HTMLElement | null;
    private navContent: HTMLElement | null;
    private mobileMenuOverlay: HTMLElement | null;
    private mobileMenuClose: HTMLElement | null;
    private scrollThreshold: number;

    constructor(scrollThreshold: number = 50) {
        this.navbar = document.querySelector('.navbar');
        this.mobileMenuToggle = document.getElementById('mobile-menu-toggle');
        this.navContent = document.getElementById('nav-content');
        this.mobileMenuOverlay = document.getElementById('mobile-menu-overlay');
        this.mobileMenuClose = document.getElementById('mobile-menu-close');
        this.scrollThreshold = scrollThreshold;

        // console.log('OptimizedNavbar elements:', {
        //     navbar: this.navbar,
        //     mobileMenuToggle: this.mobileMenuToggle,
        //     navContent: this.navContent,
        //     mobileMenuOverlay: this.mobileMenuOverlay,
        //     mobileMenuClose: this.mobileMenuClose
        // });

        this.init();
    }

    private init(): void {
        this.initScrollEffect();
        this.initMobileMenu();
        this.initMobileSubmenus();
        this.initKeyboardHandlers();
    }

    /**
     * Initialize navbar scroll effect
     */
    private initScrollEffect(): void {
        if (!this.navbar) return;

        const handleScroll = (): void => {
            // Only apply scroll effect on desktop (lg and up)
            if (window.innerWidth >= 1024) {
                if (window.scrollY > this.scrollThreshold) {
                    this.navbar?.classList.add('navbar-scrolled');
                } else {
                    this.navbar?.classList.remove('navbar-scrolled');
                }
            } else {
                // Remove scroll effect on mobile
                this.navbar?.classList.remove('navbar-scrolled');
            }
        };

        // Throttle scroll events for better performance
        let ticking = false;
        const throttledScroll = (): void => {
            if (!ticking) {
                requestAnimationFrame(() => {
                    handleScroll();
                    ticking = false;
                });
                ticking = true;
            }
        };

        window.addEventListener('scroll', throttledScroll, { passive: true });
        
        // Handle window resize to manage scroll effect on screen size changes
        window.addEventListener('resize', () => {
            handleScroll();
        }, { passive: true });
        
        // Check initial scroll position
        handleScroll();
    }

    /**
     * Initialize mobile menu functionality
     */
    private initMobileMenu(): void {
        if (!this.mobileMenuToggle || !this.navContent || !this.mobileMenuOverlay) return;

        // Toggle mobile menu
        this.mobileMenuToggle.addEventListener('click', () => {
            // console.log('Mobile menu toggle clicked');
            this.toggleMobileMenu();
        });

        // Close mobile menu
        this.mobileMenuClose?.addEventListener('click', () => {
            this.closeMobileMenu();
        });

        // Close on overlay click
        this.mobileMenuOverlay.addEventListener('click', () => {
            this.closeMobileMenu();
        });
    }

    /**
     * Initialize mobile submenu toggles.
     * First click on parent link: expand submenu. Second click (when open): navigate.
     * Chevron always toggles only (never navigates).
     */
    private initMobileSubmenus(): void {
        const navLinks = document.querySelectorAll('.nav-link');
        const clickTimers = new Map<HTMLElement, number>();
        
        navLinks.forEach(link => {
            const menuItem = link.closest('li');
            const submenu = menuItem?.querySelector('.submenu-content') as HTMLElement | null;
            const toggle = menuItem?.querySelector('.mobile-submenu-toggle');
            const hasSubmenu = !!submenu;
            
            if (!hasSubmenu) return;
            
            link.addEventListener('click', (e) => {
                if (window.innerWidth >= 1024) return;

                // Parent link label was tapped (chevron clicks are handled by toggle listener with stopPropagation)
                e.preventDefault();
                const linkEl = link as HTMLElement;
                const isOpen = submenu?.style.display === 'block';
                
                if (isOpen) {
                    // Second click (submenu already open) → navigate
                    window.location.href = link.getAttribute('href') || '#';
                    return;
                }
                
                const now = Date.now();
                const last = clickTimers.get(linkEl) ?? 0;
                if (now - last < 500) {
                    // Double-tap while closed → navigate
                    clickTimers.delete(linkEl);
                    window.location.href = link.getAttribute('href') || '#';
                    return;
                }
                
                // Single tap while closed → expand submenu
                clickTimers.set(linkEl, now);
                setTimeout(() => {
                    if (clickTimers.get(linkEl) === now) {
                        this.handleSubmenuToggle(menuItem as HTMLElement, submenu as HTMLElement, toggle as HTMLElement, link);
                    }
                }, 500);
            });
            
            if (toggle) {
                toggle.addEventListener('click', (e) => {
                    if (window.innerWidth >= 1024) return;
                    e.preventDefault();
                    e.stopPropagation();
                    this.handleSubmenuToggle(menuItem as HTMLElement, submenu as HTMLElement, toggle as HTMLElement, link);
                });
            }
        });
    }
    
    /**
     * Handle submenu toggle logic
     */
    private handleSubmenuToggle(menuItem: HTMLElement, submenu: HTMLElement, toggle: HTMLElement, navLink: Element): void {
        // Close other open submenus
        this.closeOtherSubmenus(menuItem);
        
        // Toggle current submenu
        const isCurrentlyOpen = submenu.style.display === 'block';
        // console.log('Toggling submenu, currently open:', isCurrentlyOpen);
        
        if (isCurrentlyOpen) {
            // Close submenu
            submenu.style.display = 'none';
            toggle.classList.remove('active');
            navLink.setAttribute('aria-expanded', 'false');
            // console.log('Closed submenu');
        } else {
            // Open submenu
            submenu.style.display = 'block';
            submenu.style.opacity = '1';
            submenu.style.visibility = 'visible';
            toggle.classList.add('active');
            navLink.setAttribute('aria-expanded', 'true');
            // console.log('Opened submenu');
        }
    }

    /**
     * Initialize keyboard handlers
     */
    private initKeyboardHandlers(): void {
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape') {
                this.closeMobileMenu();
            }
        });
    }

    /**
     * Toggle mobile menu visibility
     */
    private toggleMobileMenu(): void {
        const isOpen = !this.navContent?.classList.contains('-translate-y-full');
        
        if (isOpen) {
            this.closeMobileMenu();
        } else {
            this.openMobileMenu();
        }
    }

    /**
     * Open mobile menu
     */
    private openMobileMenu(): void {
        // console.log('Opening mobile menu');
        this.mobileMenuToggle?.classList.add('active');
        this.navContent?.classList.remove('-translate-y-full');
        this.navContent?.classList.add('translate-y-0');
        this.mobileMenuOverlay?.classList.remove('opacity-0', 'invisible');
        this.mobileMenuOverlay?.classList.add('opacity-100', 'visible');
        document.body.classList.add('overflow-hidden');
    }

    /**
     * Close mobile menu
     */
    private closeMobileMenu(): void {
        // console.log('Closing mobile menu');
        this.mobileMenuToggle?.classList.remove('active');
        this.navContent?.classList.remove('translate-y-0');
        this.navContent?.classList.add('-translate-y-full');
        this.mobileMenuOverlay?.classList.remove('opacity-100', 'visible');
        this.mobileMenuOverlay?.classList.add('opacity-0', 'invisible');
        document.body.classList.remove('overflow-hidden');
        
        // Close all submenus
        this.closeAllSubmenus();
    }

    /**
     * Close other open submenus
     */
    private closeOtherSubmenus(currentMenuItem: HTMLElement): void {
        const allMenuItems = document.querySelectorAll('.main-menu > li');
        
        allMenuItems.forEach(item => {
            if (item !== currentMenuItem) {
                const submenu = item.querySelector('.submenu-content') as HTMLElement;
                const toggle = item.querySelector('.mobile-submenu-toggle');
                const navLink = item.querySelector('.nav-link');
                
                if (submenu && toggle && navLink) {
                    submenu.style.display = 'none';
                    toggle.classList.remove('active');
                    navLink.setAttribute('aria-expanded', 'false');
                }
            }
        });
    }

    /**
     * Close all submenus
     */
    private closeAllSubmenus(): void {
        const allSubmenus = document.querySelectorAll('.submenu-content') as NodeListOf<HTMLElement>;
        const allToggles = document.querySelectorAll('.mobile-submenu-toggle');
        const allNavLinks = document.querySelectorAll('.nav-link');
        
        allSubmenus.forEach(submenu => {
            submenu.style.display = 'none';
        });
        allToggles.forEach(toggle => toggle.classList.remove('active'));
        allNavLinks.forEach(link => link.setAttribute('aria-expanded', 'false'));
    }

    /**
     * Public method to programmatically close mobile menu
     */
    public close(): void {
        this.closeMobileMenu();
    }

    /**
     * Public method to check if mobile menu is open
     */
    public isMobileMenuOpen(): boolean {
        return !this.navContent?.classList.contains('-translate-y-full');
    }

    /**
     * Public method to update scroll threshold
     */
    public setScrollThreshold(threshold: number): void {
        this.scrollThreshold = threshold;
    }
}

/**
 * Initialize optimized navbar when DOM is ready
 */
export function initOptimizedNavbar(scrollThreshold?: number): OptimizedNavbar {
    return new OptimizedNavbar(scrollThreshold);
}
